import { useMemo } from "react";
import { useDebouncedValue } from "@visioni/a3s-ui/hooks";
import { toISODateString } from "@visioni/a3s-codecs";
import type { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import {
  blocksCompilationRequest,
  toClipCountEstimate,
  type ClipCountEstimate,
} from "../components/RequestCompilationForm";
import { useClipRangeCount } from "./use-clip-range-count";

/**
 * How long the pending selection must hold still before it is counted.
 *
 * The time fields are masked as they are typed, so a single entry passes
 * through several complete-but-transient windows (`00:1` → `00:15`). Counting
 * each one would fire a query per keystroke.
 */
const COUNT_DEBOUNCE_MS = 400;

/**
 * Stands in for the window while the form has no countable selection. The
 * query is disabled in that state, so this is only ever a query key — never a
 * request.
 */
const UNCOUNTED_WINDOW = {
  startTime: toISODateString(new Date(0)),
  endTime: toISODateString(new Date(0)),
};

export interface UseCompilationClipCountOptions {
  /** The site the camera belongs to; the count is camera-scoped. */
  readonly siteId: SiteId | undefined;
  readonly assetCameraKey: AssetCameraKey | undefined;
  /** The window to count, or `null` while the form's selection is incomplete. */
  readonly startTime: Date | null;
  readonly endTime: Date | null;
  /**
   * Whether the count is wanted at all. Pass the dialog's own open state: the
   * form keeps its selection after the dialog closes, and counting a range
   * nobody is looking at only costs requests.
   */
  readonly enabled?: boolean | undefined;
}

export interface UseCompilationClipCountResult {
  readonly estimate: ClipCountEstimate;
  /** Whether the compilation must not be requested on this count yet. */
  readonly blocksRequest: boolean;
}

/**
 * Counts the clips a pending clip compilation request would cover, for the
 * request dialog to show before the job is queued.
 *
 * The number is an estimate, and deliberately so: a compilation is rendered
 * from the camera's own **Recordings**, while this counts the **Clips** the
 * platform has metadata for. The two cover the same window but are not the
 * same set — clip metadata also syncs on a delay, so a window ending minutes
 * ago can undercount.
 */
export function useCompilationClipCount(options: UseCompilationClipCountOptions): UseCompilationClipCountResult {
  const { siteId, assetCameraKey, startTime, endTime, enabled = true } = options;

  const startIso = startTime ? toISODateString(startTime) : undefined;
  const endIso = endTime ? toISODateString(endTime) : undefined;

  // Memoised so the debounce below has something stable to settle on: the
  // window is rebuilt from the form's fields on every render, and a fresh
  // object each time would restart the timer forever.
  const selection = useMemo(
    () =>
      enabled && siteId && assetCameraKey && startIso && endIso ?
        { siteId, assetCameraKey, startTime: startIso, endTime: endIso }
      : null,
    [enabled, siteId, assetCameraKey, startIso, endIso],
  );

  const settled = useDebouncedValue(selection, COUNT_DEBOUNCE_MS);

  const query = useClipRangeCount({
    ...(settled ?
      { siteId: settled.siteId, assetCameraKey: settled.assetCameraKey }
    : { siteId: undefined, assetCameraKey: undefined }),
    startTime: settled?.startTime ?? UNCOUNTED_WINDOW.startTime,
    endTime: settled?.endTime ?? UNCOUNTED_WINDOW.endTime,
    enabled: settled !== null,
  });

  // `isPending`, not `isFetching`: a background refetch (on window focus, say)
  // still has last count in hand, and treating that as "counting" would grey
  // the request button out from under a user who had it enabled.
  const estimate = toClipCountEstimate({
    selected: selection !== null,
    settled: settled === selection,
    isPending: query.isPending,
    isError: query.isError,
    count: query.data,
  });

  return { estimate, blocksRequest: blocksCompilationRequest(estimate) };
}
